import { Component } from '@theme/component';
import { buildSectionSelector } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      this.#loadRecommendations();
      break;
    }
  });

  /** @type {Record<string, string>} */
  #cachedRecommendations = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    if (this.dataset.recommendationsPerformed === 'true') {
      this.#fixCompactCardMedia();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

    if (result.data?.trim().length) {
          this.#replaceRecommendations(sectionId, result.data);
          this.dataset.recommendationsPerformed = 'true';
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * @param {string} sectionId
   * @param {string} html
   */
  #replaceRecommendations(sectionId, html) {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const hydrationKey = this.getAttribute('data-hydration-key');
    const elementId = this.id;

    /** @type {Element | null} */
    let newRecommendations = null;

    if (elementId) {
      newRecommendations = fragment.getElementById(elementId);
    }

    if (!newRecommendations && hydrationKey) {
      newRecommendations = fragment.querySelector(
        `product-recommendations[data-hydration-key="${hydrationKey}"]`
      );
    }

    const sectionElement = fragment.getElementById(buildSectionSelector(sectionId));
    if (!newRecommendations && sectionElement) {
      if (hydrationKey) {
        newRecommendations = sectionElement.querySelector(
          `product-recommendations[data-hydration-key="${hydrationKey}"]`
        );
      }
      if (!newRecommendations && elementId) {
        newRecommendations = sectionElement.querySelector(`#${CSS.escape(elementId)}`);
      }
      if (!newRecommendations) {
        newRecommendations = sectionElement.querySelector('product-recommendations');
      }
    }

    if (!newRecommendations) {
      newRecommendations = fragment.querySelector('product-recommendations');
    }

    if (!newRecommendations) {
      throw new Error('Product recommendations element not found in the section rendering response');
    }

    const sectionWrapper = this.closest('.shopify-section');
    if (sectionWrapper && sectionElement) {
      this.#injectSectionStylesheet(fragment, sectionWrapper);
    }

    const currentOutput = this.querySelector('[data-pr-recommendations-output]');
    const newOutput = newRecommendations.querySelector('[data-pr-recommendations-output]');

    if (currentOutput && newOutput) {
      currentOutput.replaceWith(newOutput.cloneNode(true));
    } else {
      this.innerHTML = newRecommendations.innerHTML;
    }

    for (const attribute of newRecommendations.attributes) {
      if (attribute.name === 'id') continue;
      this.setAttribute(attribute.name, attribute.value);
    }

    this.#fixCompactCardMedia();
  }

  /**
   * Product-page Liquid can leak the open PDP image into recommendation cards.
   * Fetch each product by handle and set the correct featured image.
   * @param {ParentNode} [root]
   */
  #fixCompactCardMedia(root = this) {
    const cards = root.querySelectorAll('.pr-compact-card[data-reco-handle]');
    const shopifyRoot = /** @type {{ routes?: { root?: string } }} */ (window.Shopify)?.routes?.root ?? '/';

    cards.forEach((card) => {
      const handle = /** @type {HTMLElement} */ (card).dataset.recoHandle;
      const image = /** @type {HTMLImageElement | null} */ (card.querySelector('.pr-compact-card__image'));

      if (!handle || !image || image.dataset.mediaFixed === 'true') return;

      fetch(`${shopifyRoot}products/${handle}.js`)
        .then((response) => {
          if (!response.ok) throw new Error(`Product fetch failed: ${response.status}`);
          return response.json();
        })
        .then((/** @type {{ featured_image?: string; images?: string[]; title?: string }} */ product) => {
          const imageUrl = product.featured_image || product.images?.[0];
          if (!imageUrl) return;

          image.src = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
          image.removeAttribute('srcset');
          image.dataset.mediaFixed = 'true';

          if (product.title) {
            image.alt = product.title;
          }
        })
        .catch(() => {});
    });
  }

  /**
   * @param {Document} fragment
   * @param {HTMLElement} sectionElement
   */
  #injectSectionStylesheet(fragment, sectionElement) {
    const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
    if (!newStylesheet) return;

    const existingStylesheet = sectionElement.querySelector('style[data-section-stylesheet]');

    if (existingStylesheet) {
      existingStylesheet.textContent = newStylesheet.textContent;
    } else {
      sectionElement.prepend(newStylesheet);
    }
  }

  /**
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error.message);
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
